import { useEffect, useState } from "react";

import { apiFetch } from "@/lib/api-client";
import { Skeleton, SkeletonCard } from "@/components/skeleton";

interface AdminOverviewSummary {
  activeTenants: number;
  totalMrrCents: number;
  eventsToday: number;
  totalWorkers: number;
  healthyWorkers: number;
  errorCount1h: number;
  p95LatencyMs: number;
  analyticsAvailable: boolean;
}

interface WorkerHealth {
  name: string;
  status: "healthy" | "degraded" | "down" | "unknown";
  lastCheckAt: string | null;
}

interface HealthResponse {
  summary: {
    totalWorkers: number;
    healthyCount: number;
    errorRate1h: number;
    p95LatencyMs: number;
  };
  workers: WorkerHealth[];
}

function formatCurrency(cents: number) {
  return new Intl.NumberFormat("en-IE", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function OverviewSkeleton() {
  return (
    <div className="space-y-6">
      <div>
        <Skeleton className="h-8 w-56" />
        <Skeleton className="mt-2 h-4 w-80" />
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <SkeletonCard key={index} className="h-28" />
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Skeleton className="h-40 w-full rounded-lg" />
        <Skeleton className="h-40 w-full rounded-lg" />
      </div>
    </div>
  );
}

export default function AdminOverviewPage() {
  const [summary, setSummary] = useState<AdminOverviewSummary | null>(null);
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isCancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);

      const [statsResult, healthResult] = await Promise.allSettled([
        apiFetch<{ summary: AdminOverviewSummary }>("/api/admin/operations/stats"),
        apiFetch<HealthResponse>("/api/admin/operations/health"),
      ]);

      if (isCancelled) {
        return;
      }

      if (statsResult.status === "fulfilled") {
        setSummary(statsResult.value.summary);
      } else {
        setSummary(null);
      }

      if (healthResult.status === "fulfilled") {
        setHealth(healthResult.value);
      } else {
        setHealth(null);
      }

      if (statsResult.status === "rejected" && healthResult.status === "rejected") {
        setError("Platform overview is currently unavailable.");
      }

      setIsLoading(false);
    };

    void load();

    return () => {
      isCancelled = true;
    };
  }, []);

  if (isLoading) {
    return <OverviewSkeleton />;
  }

  const stats = [
    {
      label: "Active Tenants",
      value: summary ? String(summary.activeTenants) : "—",
      detail: "Control-plane tenants with active status",
    },
    {
      label: "Total MRR",
      value: summary ? formatCurrency(summary.totalMrrCents) : "—",
      detail: "Monthly recurring revenue across active subscriptions",
    },
    {
      label: "Events Today",
      value: summary ? String(summary.eventsToday) : "—",
      detail: summary?.analyticsAvailable ? "Live ClickHouse event volume since midnight" : "ClickHouse unavailable",
    },
    {
      label: "Worker Health",
      value: summary ? `${summary.healthyWorkers}/${summary.totalWorkers}` : "—",
      detail: summary ? `${summary.errorCount1h} errors in the last hour` : "Health checks unavailable",
    },
  ];

  const workerRows = health?.workers.slice(0, 5) ?? [];

  return (
    <div>
      <h1 className="text-2xl font-bold text-white">Platform Overview</h1>
      <p className="mt-1 text-muted-foreground">Cross-tenant platform health and metrics</p>

      {error ? (
        <div className="mt-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-4 text-sm text-muted-foreground">
          {error}
        </div>
      ) : null}

      <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-4">
            <p className="text-xs text-muted-foreground">{stat.label}</p>
            <p className="mt-1 text-2xl font-bold text-white">{stat.value}</p>
            <p className="mt-1 text-xs text-muted-foreground">{stat.detail}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 grid gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-4">
          <h3 className="font-semibold text-white">Recent Activity</h3>
          <div className="mt-3 space-y-2 text-sm text-muted-foreground">
            <p>{summary ? `${summary.eventsToday} platform events recorded today.` : "Event metrics are currently unavailable."}</p>
            <p>{summary ? `${formatCurrency(summary.totalMrrCents)} current recurring revenue across ${summary.activeTenants} active tenants.` : "Revenue metrics are currently unavailable."}</p>
            <p>{summary ? `${summary.errorCount1h} worker errors logged in the last hour.` : "Worker error summary is currently unavailable."}</p>
          </div>
        </div>

        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-4">
          <h3 className="font-semibold text-white">System Health</h3>
          <div className="mt-2 space-y-2">
            {workerRows.length > 0 ? (
              workerRows.map((worker) => (
                <div key={worker.name} className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{worker.name}</span>
                  <span className={`flex items-center gap-1 text-xs ${
                    worker.status === "healthy" ? "text-[var(--color-success)]" : "text-[var(--color-warning)]"
                  }`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${
                      worker.status === "healthy" ? "bg-[var(--color-success)]/40" : "bg-[var(--color-warning)]/40"
                    }`} />
                    {worker.status}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">Health checks are currently unavailable.</p>
            )}
          </div>
          {health ? (
            <p className="mt-3 text-xs text-muted-foreground">
              P95 latency {health.summary.p95LatencyMs}ms across {health.summary.totalWorkers} workers.
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
